import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, ChevronDown, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-event "Join Waitlist" form — shown on the event detail page when the event
 * has `waitlist_only=true`. Captures name/email/phone plus 21+ and T&C consent,
 * fires a confirmation email to the signup and an admin notification.
 *
 * Matches the client wireframe: monospace, terminal-brackets aesthetic, expandable
 * T&C blurb, big [JOIN WAITLIST] button.
 */
const EventWaitlistForm = ({ event }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [agreedAge, setAgreedAge] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { queue_number }

  const minAge = event.min_age || 21;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !phone.trim()) {
      toast.error('Fill in your name, email and phone');
      return;
    }
    if (!agreedAge || !agreedTerms) {
      toast.error(`You must confirm you're ${minAge}+ and agree to the T&C`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${event.id}/waitlist`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        agreed_age: agreedAge,
        agreed_terms: agreedTerms,
      });
      setSuccess({ queue_number: res.data.queue_number });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong, try again');
    } finally {
      setSubmitting(false);
    }
  };

  const eventLine = `${event.venue} · ${new Date(event.date + 'T' + (event.time || '00:00')).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })} · ${event.time || ''}`.trim();

  if (success) {
    return (
      <div
        className="bg-[#0A0A0A] border border-[#B5FF00]/50 p-6 md:p-8 text-center"
        data-testid="waitlist-success-card"
      >
        <div className="w-16 h-16 rounded-full bg-[#B5FF00]/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={40} className="text-[#B5FF00]" strokeWidth={1.5} />
        </div>
        <h3 className="font-['Josefin_Sans'] text-2xl text-white mb-2">YOU&apos;RE IN</h3>
        <p className="font-mono text-xs text-[#B5FF00] tracking-widest mb-4">
          QUEUE #{success.queue_number}
        </p>
        <p className="text-zinc-400 text-sm">
          We&apos;ll email you the moment tickets go live for <b className="text-white">{event.title}</b>.
        </p>
        <p className="text-zinc-600 text-xs font-mono mt-4 tracking-wide">
          Waitlist doesn&apos;t guarantee entry — final approval is at management discretion.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-[#0A0A0A] border border-zinc-800 p-6 md:p-8 font-mono"
      data-testid="event-waitlist-form"
    >
      {/* Header bracket — matches wireframe */}
      <div className="mb-6 pb-4 border-b border-zinc-800">
        <h2
          className="font-['Josefin_Sans'] text-2xl md:text-3xl font-bold text-white tracking-wide"
          data-testid="waitlist-heading"
        >
          JOIN THE WAITLIST
        </h2>
        <p className="text-[#F4A340] text-sm mt-2 tracking-wide" data-testid="waitlist-event-line">
          {event.title} · {eventLine}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs text-zinc-400 tracking-widest mb-1 block">FULL NAME</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
            required
            data-testid="waitlist-name-input"
          />
        </div>
        <div>
          <label className="text-xs text-zinc-400 tracking-widest mb-1 block">EMAIL</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
            required
            data-testid="waitlist-email-input"
          />
        </div>
        <div>
          <label className="text-xs text-zinc-400 tracking-widest mb-1 block">PHONE NUMBER</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
            required
            data-testid="waitlist-phone-input"
          />
        </div>
      </div>

      {/* Consent checkboxes */}
      <div className="mt-6 pt-4 border-t border-zinc-800 space-y-3">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="waitlist-age-checkbox-label">
          <input
            type="checkbox"
            checked={agreedAge}
            onChange={(e) => setAgreedAge(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#B5FF00]"
            data-testid="waitlist-age-checkbox"
          />
          <span className="text-sm text-zinc-300">
            I am <span className="text-[#F4A340] font-bold">{minAge}+</span> years old
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer" data-testid="waitlist-terms-checkbox-label">
          <input
            type="checkbox"
            checked={agreedTerms}
            onChange={(e) => setAgreedTerms(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#B5FF00]"
            data-testid="waitlist-terms-checkbox"
          />
          <span className="text-sm text-zinc-300">
            I agree to <span className="text-white">Terms &amp; Conditions</span>
          </span>
        </label>
      </div>

      {/* Collapsible T&C */}
      <button
        type="button"
        onClick={() => setShowTerms((s) => !s)}
        className="mt-4 w-full flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-xs tracking-widest border border-zinc-800 px-3 py-2"
        data-testid="waitlist-terms-toggle"
      >
        <ChevronDown
          size={14}
          className={`transition-transform ${showTerms ? 'rotate-180' : ''}`}
          strokeWidth={1.5}
        />
        TERMS &amp; CONDITIONS
      </button>
      {showTerms && (
        <div
          className="mt-2 p-4 bg-[#050505] border border-zinc-800 text-xs text-zinc-400 leading-relaxed whitespace-pre-line"
          data-testid="waitlist-terms-body"
        >
          {event.terms_conditions || (
            'Entry subject to venue approval and management discretion. Waitlist does not ' +
            'guarantee entry. Age verification required at the door. Management reserves ' +
            'the right of admission.'
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || !agreedAge || !agreedTerms}
        className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="waitlist-submit-btn"
      >
        {submitting ? (
          <>
            <Loader2 size={16} className="animate-spin" /> JOINING...
          </>
        ) : (
          <>[ JOIN WAITLIST ]</>
        )}
      </button>
    </form>
  );
};

export default EventWaitlistForm;
